import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { FC } from 'react';
import Image from 'next/image';
import { SxProps, Theme } from '@mui/material/styles';
import { palette } from '@/theme/constants';

interface IAditionalProps {
  icon?: string;
  endIcon?: string; // New prop for end icon
  background?: string;
  width?: string | number;
  height?: string | number;
  iconWidth?: number;
  iconHeight?: number;
  endIconWidth?: number;
  endIconHeight?: number;
  padding?: string | number;
  disabled?: boolean;
  altIcon?: string;
  altEndIcon?: string;
  label?: string;
  labelColor?: string;
  iconPosition?: 'left' | 'right';
  direction?: string;
  method?: (event?: any) => void;
  positionIcon?: string;
  loading?: any;
  isRounded?: boolean;
  borderRadius?: any;
  disableRipple?: boolean;
  borderColor?: string;
  sx?: SxProps<Theme>;
}

export const MuiIconButton: FC<IAditionalProps> = ({
  icon,
  endIcon, // New prop
  borderRadius,
  background,
  disabled = false,
  loading,
  direction,
  width = 32,
  height = 32,
  padding = 1,
  altIcon,
  altEndIcon,
  iconWidth = 16,
  endIconWidth = 16, // Default width for end icon
  iconHeight = 16,
  endIconHeight = 16, // Default height for end icon
  label,
  labelColor = palette.white,
  method,
  iconPosition = 'right',
  positionIcon = 'center',
  isRounded = true,
  disableRipple = false,
  borderColor,
  sx,
}: IAditionalProps) => {
  const darkenHexColor = (hex: string, darkenFactor: number) => {
    let r = parseInt(hex.slice(1, 3), 16),
      g = parseInt(hex.slice(3, 5), 16),
      b = parseInt(hex.slice(5, 7), 16);

    r = Math.max(0, r - darkenFactor);
    g = Math.max(0, g - darkenFactor);
    b = Math.max(0, b - darkenFactor);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const iconStyle = {
    width,
    height,
    gap: direction ? '0.2rem' : '',
    background,
    padding,
    display: 'flex',
    justifyContent: 'center',
    alignItems: positionIcon,
    borderRadius: isRounded ? '50%' : borderRadius,
    border: `0.063rem solid ${borderColor}`,
    '&:disabled': {
      backgroundColor: '#0F1235',
      color: '#FFFFFF',
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: background ? darkenHexColor(background, 35) : '',
    },
    ...sx,
  };

  return (
    <Box
      display='flex'
      flexDirection={direction ? 'row' : 'column'}
      data-cy='iconButton'
      justifyContent='center'
      alignItems='center'>
      <IconButton sx={iconStyle} onClick={method} disabled={disabled} disableRipple={disableRipple}>
        {!loading ? (
          <>
            {direction && label && iconPosition === 'right' && (
              <Typography  sx={{ fontFamily: '"Inter", sans-serif !important',}} fontSize={'1rem'}  color={labelColor}>
                {label}
              </Typography>
            )}
            {icon && altIcon && (
              <Image priority src={`${icon}.svg`} alt={altIcon} width={iconWidth} height={iconHeight} quality={80} />
            )}
            {direction && label && iconPosition === 'left' && (
              <Typography sx={{ fontFamily: '"Inter", sans-serif !important',}} fontSize={'1rem'}  color={labelColor}>
                {label}
              </Typography>
            )}
            {/* End Icon (only if provided) */}
            {endIcon && altEndIcon && (
              <Image
                style={{ marginLeft: '0.4rem' }}
                priority
                src={`${endIcon}.svg`}
                alt={altEndIcon}
                width={endIconWidth}
                height={endIconHeight}
                quality={80}
              />
            )}
          </>
        ) : (
          <CircularProgress size={32} sx={{ color: '#fff' }} />
        )}
      </IconButton>

      {!direction && label && (
        <Typography  sx={{ fontFamily: '"Inter", sans-serif !important',}} fontSize={'1rem'} color={labelColor}>
          {label}
        </Typography>
      )}
    </Box>
  );
};
